import { platform, websiteStatus } from '@va/constants';
import { useTranslate } from '@va/localization';
import { SeeMoreHorizontally } from '@va/svg-visa-icons';
import { Button, fontWeights, Paragraph, STATUS_COLORS, StatusPill } from '@va/ui/design-system';
import { getUrlWithoutProtocolAndTrailingSlash } from '@va/util/helpers';
import classNames from 'classnames';
import { memo, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { switchActiveWebsite } from '@va/dashboard/actions/app';
import { isMediumDevice as isMediumDeviceSelector, isTabletDevice } from '@va/dashboard/selectors/ui';
import { UserRole } from '@va/types/website';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useRefreshCallbacks, useRefreshContext } from '@va/util/components';
import { useStartIntersectionObserverCtx } from '@va/util/misc';
import { MappedWebsite } from '../../agency-white-label-v2/agency-white-label-apiClient';
import BillingPlan from './BillingPlan';
import Overview from './Overview';
import PendingContributorRequest from './PendingContributorRequest';
import VisitorsChart, { WebsiteChartDataset } from './VisitorsChart';
import WebsiteActions from './WebsiteActions';
import './WebsitesTableRow.scss';
import { useCurrentWebsiteActivityData, usePreviousWebsiteActivityData } from './apiClient';

const WebsitesTableRow = ({ website, index }: { website: MappedWebsite; index: number }) => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const { isManualRefreshing } = useRefreshContext();

  const isTablet = useSelector(isTabletDevice);
  const isMediumDevice = useSelector(isMediumDeviceSelector);

  const manageWebsiteRef = useRef<HTMLDivElement>(null);

  const [isWebsiteActionsVisible, setWebsiteActionsVisible] = useState(false);
  const [hiddenChartDatasets, setHiddenChartDatasets] = useState<WebsiteChartDataset[]>([]);

  const { id: websiteId, userRole, trackingCodeInstalled, platform: platformName } = website;
  const isWebsiteOwner = userRole === UserRole.OWNER;
  const pendingContributorRequest = !isWebsiteOwner && website.contributorRequests.length > 0;

  const {
    data: activityData,
    error: activityDataError,
    isLoading: isActivityDataLoading,
    mutate: mutateActivityData,
  } = useCurrentWebsiteActivityData(websiteId);
  const {
    data: prevActivityData,
    error: prevActivityDataError,
    isLoading: isPrevActivityDataLoading,
    mutate: mutatePrevActivityData,
  } = usePreviousWebsiteActivityData(websiteId);

  useStartIntersectionObserverCtx(manageWebsiteRef);
  useRefreshCallbacks(mutateActivityData, mutatePrevActivityData);

  const inProgress = useMemo(
    () => isManualRefreshing || isActivityDataLoading || isPrevActivityDataLoading,
    [isActivityDataLoading, isManualRefreshing, isPrevActivityDataLoading],
  );

  function handleSelectedWebsite() {
    if (!isWebsiteOwner && website.status === websiteStatus.PENDING) return;
    dispatch(switchActiveWebsite({ websiteId: websiteId, handleRoute: false, fromManageWebsites: true }));
  }

  const overviewContent = (
    <Overview
      inProgress={inProgress}
      hiddenChartDatasets={hiddenChartDatasets}
      setHiddenChartDatasets={setHiddenChartDatasets}
      currentData={activityData}
      previousData={prevActivityData}
    />
  );

  return (
    <div
      ref={manageWebsiteRef}
      key={`manage-websites-website-${index}`}
      className={classNames(
        'manage-websites-wrapper flex sm:flex-col border-b-2 sm:border-2 sm:mb-4 sm:rounded-24 border-solid border-white-smoke',
        { 'min-h-[24.375rem]': !pendingContributorRequest },
        { 'min-h-[13.125rem]': pendingContributorRequest },
      )}
    >
      <div className='website-info-section p-5 border-r-2 sm:border-0 w-full border-solid border-white-smoke'>
        <div className='flex justify-between'>
          <TooltipWrapper interactive content={website.url}>
            <div
              onClick={handleSelectedWebsite}
              className='text-ellipsis text-21 leading-24 font-semibold self-center cursor-pointer'
            >
              {getUrlWithoutProtocolAndTrailingSlash(website.url)}
            </div>
          </TooltipWrapper>

          {!pendingContributorRequest && (
            <TooltipWrapper
              open={isWebsiteActionsVisible}
              content={
                <WebsiteActions
                  isSumoActive={website?.sumoActive}
                  subscription={website.subscription}
                  websiteId={websiteId}
                  whiteLabel={website.whiteLabel}
                  isWebsiteOwner={isWebsiteOwner}
                  setWebsiteActionsVisible={setWebsiteActionsVisible}
                  platformName={platformName}
                />
              }
              tooltipClassNames='rounded-12 shadow-overlay'
              trigger='click'
              onOpenChange={(open) => setWebsiteActionsVisible(open)}
              useDefaultStyle={false}
            >
              <div>
                <Button
                  color='tertiary'
                  onClick={() => setWebsiteActionsVisible(!isWebsiteActionsVisible)}
                  icon={(className, color) => <SeeMoreHorizontally className={className} color={color} />}
                />
              </div>
            </TooltipWrapper>
          )}
        </div>
        <div className='py-3 flex justify-between items-center'>
          <StatusPill
            colors={trackingCodeInstalled ? STATUS_COLORS.GREEN : STATUS_COLORS.YELLOW}
            width={'w-full'}
            className='justify-start'
          >
            {translate(`manageWebsites.website.tracking.${trackingCodeInstalled ? 'active' : 'inactive'}`)}
          </StatusPill>
          {platformName === platform.WIX && (
            <span className='border-2 px-3 py-1 text-12 rounded-12 text-ceSoir '>{platformName}</span>
          )}
        </div>

        <div className='py-3'>
          <div className='text-12 leading-18 text-gray-dusty'>
            {translate('card.manageWebsites.header.websiteRole')}:
          </div>
          <div className='text-15 leading-24 font-semibold'>
            {translate(`card.manageWebsites.website.role.${userRole}`)}
          </div>
        </div>

        <div className='flex justify-between py-3'>
          {isWebsiteOwner && (
            <div>
              <div className='text-12 text-gray-dusty'>{translate('card.manageWebsites.header.whiteLabel')}:</div>
              <div className='text-15 leading-24 font-semibold'>
                {translate(`option.btn.${website.whiteLabel ? 'enabled' : 'disabled'}`)}
              </div>
            </div>
          )}

          <div>
            <div className='text-12 text-gray-dusty'>{translate('card.manageWebsites.header.plan')}:</div>
            <div className='text-15 leading-24 font-semibold'>
              {website.unlimitedPlanActive ? (
                <Paragraph weight={fontWeights.medium} colorClassName='text-green-green'>
                  {translate('plan.unlimited')}
                </Paragraph>
              ) : (
                <BillingPlan website={website} />
              )}
            </div>
          </div>
        </div>
        {!isMediumDevice && isTablet && !pendingContributorRequest && overviewContent}
      </div>

      {pendingContributorRequest ? (
        <div className='w-full h-full bg-gray-alabaster border-solid border-2 sm:rounded-b-24 border-white min-h-[13.125rem]'>
          <PendingContributorRequest requestId={website.contributorRequests[0].id} userRole={userRole!} />
        </div>
      ) : (
        <>
          {(!isTablet || isMediumDevice) && (
            <div className='visitors-overview-section relative p-6u w-full sm:w-auto border-r-2 sm:border-t-2 sm:border-r-0 border-solid border-white-smoke'>
              {overviewContent}
            </div>
          )}

          <div className='relative p-5 w-full block sm:hidden min-h-[24.375rem] overflow-hidden'>
            <VisitorsChart
              websiteUrl={website.url}
              inProgress={inProgress}
              data={activityData}
              isError={!!activityDataError || !!prevActivityDataError}
              hiddenChartDatasets={hiddenChartDatasets}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default memo(WebsitesTableRow);
